import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Label,
} from '@repo/shadcn';
import { useDebounce } from '@uidotdev/usehooks';
import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { PiPlusCircleDuotone } from 'react-icons/pi';

import { AutoCompleteInput } from '../../../../components/ui/autocomplete/auto-complete-input';
import { MODAL_ROUTE } from '../../../../constants';
import { useGetGenericsQuery } from '../../../../features/slice/products/generic/generic-api-slice';
import { useModal } from '../../../../hooks';
import { TProductForm } from './product-form.component';

export const GenericSelector: FC = () => {
    const [searchInputText, setSearchInputText] = useState('');
    const { openModal } = useModal();

    const { setValue, watch, control } = useFormContext<TProductForm>();

    const selectedGenericId = watch('generic_id');
    const selectedGenericName = watch('generic_name');

    const debouncedSearchInputText = useDebounce(searchInputText, 500);

    const { data, isLoading, isFetching } = useGetGenericsQuery({
        search: debouncedSearchInputText,
    });

    const handleInputChange = (value: string) => {
        setSearchInputText(value);
    };

    const handleValueChange = (value: any) => {
        if (!value) {
            setValue('generic_id', '');
            setValue('generic_name', '');
            return;
        }

        setValue('generic_id', value.value);
        setValue('generic_name', value.label);
    };

    const generateOptions = () => {
        const options =
            data?.data.map((brand: any) => ({
                label: brand.name,
                value: brand.id,
            })) || [];

        // add selected option if not in the list
        if (
            selectedGenericId &&
            selectedGenericName &&
            !options.find((opt: any) => opt.value === selectedGenericId)
        ) {
            options.unshift({
                label: selectedGenericName,
                value: selectedGenericId,
            });
        }

        return options;
    };

    const options = generateOptions();

    const selectedOption = {
        label: selectedGenericName || '',
        value: selectedGenericId || '',
    };

    return (
        <FormField
            control={control}
            name={'brand_id'}
            render={() => (
                <FormItem>
                    <FormLabel className="flex items-center justify-between">
                        <div>
                            <span>Generic</span>
                            <span className="ml-1 text-red-500">*</span>
                        </div>

                        <Label
                            onClick={() =>
                                openModal(MODAL_ROUTE.CREATE_PRODUCT_GENERIC)
                            }
                            className="flex cursor-pointer items-center gap-1 text-primary hover:underline"
                        >
                            <PiPlusCircleDuotone size={16} className="-mt-1" />
                            Add Generic
                        </Label>
                    </FormLabel>

                    <FormControl>
                        <AutoCompleteInput
                            isLoading={isLoading || isFetching}
                            options={options}
                            value={selectedOption}
                            emptyMessage={'No items found'}
                            onValueChange={handleValueChange}
                            onInputChange={handleInputChange}
                            showSuggestionsOnFocus={true}
                        />
                    </FormControl>
                    <FormMessage className="line-clamp-1 text-xs" />
                </FormItem>
            )}
        />
    );
};

GenericSelector.displayName = 'GenericSelector';
